import React from 'react';
import { graphql } from 'gatsby';
import { withPreview } from 'gatsby-source-prismic';
import { get } from 'lodash/fp';
import Hero from '../components/Hero';
import PostCardRoll from '../components/PostCardRoll';
import References from '../components/References';
import ServicesHome from '../components/ServicesHome';
import Quote from '../components/Quote';
import AboutHome from '../components/AboutHome';
import { DIRECTION_COLUMN } from '../components/PostCard';
import { getSlug, reduceSlices } from '../utils';

export const IndexPageTemplate = ({
  heroSection,
  quoteSection,
  aboutSection,
  blogPostsSection,
  latestBlogPosts,
  myReferencesSection,
  myServicesSection,
}) => {
  return (
    <>
      {heroSection && (
        <Hero
          headline={heroSection.headline}
          subheadline={heroSection.subheadline}
          cta={heroSection.cta}
          imageLandscape={heroSection.imagelandscape}
          imagePortrait={heroSection.imageportrait}
        />
      )}
      {myServicesSection && (
        <div className="py-12 bg-gray-100">
          <ServicesHome
            headline={myServicesSection.headline}
            services={myServicesSection.services}
            cta={myServicesSection.cta}
            className="container mx-auto px-4"
          />
        </div>
      )}
      {myReferencesSection && (
        <div className="py-12 px-4">
          <References
            headline={myReferencesSection.headline}
            subheadline={myReferencesSection.subheadline}
            references={myReferencesSection.references}
          />
        </div>
      )}
      {blogPostsSection && (
        <div className="bg-gray-100 py-12">
          <PostCardRoll
            headline={blogPostsSection.headline}
            posts={latestBlogPosts}
            cta={blogPostsSection.cta}
            direction={DIRECTION_COLUMN}
            displayLikes
            displayDescription
            className="container mx-auto px-4"
          />
        </div>
      )}
      {quoteSection && (
        <Quote
          imageLandscape={quoteSection.imagelandscape}
          imagePortrait={quoteSection.imageportrait}
          headline={quoteSection.headline}
        />
      )}
      {aboutSection && (
        <div className="pt-16 pb-8 lg:py-16 bg-gray-100">
          <AboutHome
            headline={aboutSection.headline}
            text={aboutSection.text}
            image={aboutSection.image}
            cta={aboutSection.cta}
            className="container mx-auto px-4"
          />
        </div>
      )}
    </>
  );
};

const getCtaTransformed = primary => {
  return {
    label: get('cta_label', primary),
    link: get('cta_link', primary),
  };
};

const getServicesTransformed = ({ primary, items }) => {
  return {
    headline: get('headline', primary),
    cta: getCtaTransformed(primary),
    services: items.map(({ service }) => service.document.data),
  };
};

const getBlogTransformed = ({ primary }) => {
  return {
    headline: get('headline', primary),
    cta: getCtaTransformed(primary),
  };
};

const getLatestBlogPostsTransformed = latestBlogPosts => {
  return latestBlogPosts.map(post => ({
    node: { ...post.node.data, slug: getSlug(post.node) },
  }));
};

const getAboutTransformed = ({ primary }) => {
  return {
    ...primary,
    cta: getCtaTransformed(primary),
  };
};

const getReferencesTransformed = ({ primary, items }) => {
  return {
    ...primary,
    references: items.map(item => ({
      authorName: item.author_name,
      authorPosition: item.author_position,
      quote: item.quote,
    })),
  };
};

const IndexPage = ({ data }) => {
  const {
    prismicHomePage: {
      data: { body },
    },
    allPrismicBlogPost: { edges: latestBlogPosts },
  } = data;

  const homeBodyObj = reduceSlices(body);

  const { hero, quote, about, blog, references, services } = homeBodyObj;

  return (
    <IndexPageTemplate
      heroSection={get('primary', hero)}
      quoteSection={get('primary', quote)}
      aboutSection={getAboutTransformed(about)}
      blogPostsSection={getBlogTransformed(blog)}
      myReferencesSection={getReferencesTransformed(references)}
      myServicesSection={getServicesTransformed(services)}
      latestBlogPosts={getLatestBlogPostsTransformed(latestBlogPosts)}
    />
  );
};

export default withPreview(IndexPage);

export const pageQuery = graphql`
  query HomePageTemplate($prismicId: ID!, $lang: String, $shortLang: String) {
    prismicHomePage(prismicId: { eq: $prismicId }) {
      data {
        body {
          ... on PrismicHomePageBodyHero {
            id
            primary {
              headline
              subheadline
              imagelandscape {
                fluid(maxWidth: 1024, imgixParams: { q: 20 }) {
                  ...GatsbyPrismicImageFluid
                }
              }
              imageportrait {
                fluid(maxWidth: 384, imgixParams: { q: 20 }) {
                  ...GatsbyPrismicImageFluid
                }
              }
            }
            slice_type
          }
          ... on PrismicHomePageBodyQuote {
            id
            primary {
              headline
              imagelandscape {
                fluid(maxWidth: 1024, imgixParams: { q: 20 }) {
                  ...GatsbyPrismicImageFluid
                }
              }
              imageportrait {
                fluid(maxWidth: 384, imgixParams: { q: 20 }) {
                  ...GatsbyPrismicImageFluid
                }
              }
            }
            slice_type
          }
          ... on PrismicHomePageBodyServices {
            id
            items {
              service {
                document {
                  ... on PrismicServiceGroup {
                    id
                    data {
                      headline
                      subheadline
                      highlights {
                        text
                      }
                      prices {
                        currency
                        amount
                      }
                      cta_label
                      cta_link
                    }
                  }
                }
              }
            }
            primary {
              headline
              cta_link
              cta_label
            }
            slice_type
          }
          ... on PrismicHomePageBodyBlog {
            id
            primary {
              cta_label
              cta_link
              headline
            }
            slice_type
          }
          ... on PrismicHomePageBodyAbout {
            primary {
              headline
              cta_link
              cta_label
              text
              image {
                fluid(maxWidth: 384, maxHeight: 480) {
                  ...GatsbyPrismicImageFluid
                }
              }
            }
            slice_type
          }
          ... on PrismicHomePageBodyReferences {
            primary {
              headline
              subheadline
            }
            items {
              author_name
              author_position
              quote
            }
            slice_type
          }
        }
      }
    }
    allPrismicBlogPost(
      filter: { lang: { eq: $lang } }
      sort: { fields: first_publication_date, order: DESC }
      limit: 3
    ) {
      edges {
        node {
          id
          data {
            title
            description
            slug
            featuredimage {
              fluid(maxWidth: 384) {
                ...GatsbyPrismicImageFluid
              }
            }
            body {
              text
            }
            likes
            views
          }
        }
      }
    }
    prismicFooter(lang: { eq: $lang }) {
      ...FooterFragment
    }
    prismicDisclaimer(lang: { eq: $lang }) {
      ...PostDisclaimerFragment
    }
    prismicWhyHireMe(lang: { eq: $lang }) {
      ...WhyHireMeFragment
    }
    googleReviews: allGoogleReview(
      sort: { fields: position, order: ASC }
      limit: 3
      filter: { lang: { eq: $shortLang } }
    ) {
      edges {
        node {
          ...GoogleReviewsFragment
        }
      }
    }
  }
`;
